import mapboxgl from "mapbox-gl";

import { REPORT_SELECTION_LINE_PAINT } from "../constants";
import { resizeCanvas, squareCanvas } from "../helpers/canvas";
import { prepareSaveButton } from "../helpers/forms";
import { drawNorthStar, drawScale, loadMapImage } from "../helpers/map";
import RecenterControl from "../mapbox/controls/recenterControl";
import ApplicationController from "./application_controller";

const EXPORT_WIDTH = 1000;

const drawSmallMap = (canvas, smallMapCanvas) => {
  const c = squareCanvas(smallMapCanvas, canvas.height * 0.45);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(c, 0, 0, c.width, c.height, canvas.width - c.width, canvas.height - c.height, c.width, c.height);
  ctx.strokeStyle = "black";
  ctx.strokeRect(canvas.width - c.width, canvas.height - c.height, c.width + 5, c.height + 5);
};

export default class extends ApplicationController {
  static targets = ["bigMap", "smallMap"];
  static values = {
    geoInfo: Object,
    markerUrl: String,
    bigMapZoom: Number,
    smallMapZoom: Number,
    style: String,
  };

  connect() {
    super.connect();
    const geoInfo = this.geoInfoValue;
    const center = geoInfo.center;
    const z1 = this.bigMapZoomValue;
    const z2 = this.smallMapZoomValue;
    const style = this.styleValue;
    const markerUrl = this.markerUrlValue;
    mapboxgl.accessToken = gon.global.mapbox_token;
    this.bigMap = new mapboxgl.Map({
      container: this.bigMapTarget,
      style: style,
      center: center,
      zoom: z1,
      preserveDrawingBuffer: true,
    });

    this.smallMap = new mapboxgl.Map({
      container: this.smallMapTarget,
      style: style,
      center: center,
      zoom: z2,
      preserveDrawingBuffer: true,
    });

    [this.bigMap, this.smallMap].forEach((map) => {
      map.addControl(new mapboxgl.NavigationControl());
      map.addControl(new RecenterControl(), "top-right");
      map.addControl(new mapboxgl.ScaleControl(), "bottom-right");
      map.on("idle", () => (map.ready = true));
    });

    this.bigMap.on("load", () => {
      this.bigMap.addSource("report-selection", {
        type: "geojson",
        data: geoInfo.selection,
      });
      this.bigMap.addLayer({
        id: "report-selection-line",
        type: "line",
        source: "report-selection",
        paint: REPORT_SELECTION_LINE_PAINT,
      });
    });

    this.smallMap.on("load", async () => {
      const markerImage = await loadMapImage(this.smallMap, markerUrl);
      this.smallMap.addImage("custom-marker", markerImage);
      this.smallMap.addSource("selection-center", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [{ type: "Feature", geometry: { type: "Point", coordinates: center } }],
        },
      });
      this.smallMap.addLayer({
        id: "selection-center-marker",
        type: "symbol",
        source: "selection-center",
        layout: { "icon-image": "custom-marker" },
      });
    });
  }

  saveMaps(event) {
    event.preventDefault();
    if (![this.bigMap, this.smallMap].every((m) => m && m.ready)) {
      return;
    }
    const { signedGid, attribute } = prepareSaveButton(event);

    const canvas = resizeCanvas(this.bigMap.getCanvas(), EXPORT_WIDTH);
    drawNorthStar(this.bigMap, canvas);
    drawScale(this.bigMap, canvas, 150);
    drawSmallMap(canvas, this.smallMap.getCanvas());

    const imageBase64 = canvas.toDataURL("image/jpeg", 0.85);
    this.stimulate("FormReflex#update", signedGid, attribute, imageBase64, true);
  }
}
